import React, { useState, useEffect } from 'react';
import { auditsApi } from '../services/api';

interface AuditDetailModalProps {
  auditId: number;
  onClose: () => void;
  onStartAudit: () => void;
}

interface AuditDetail {
  id: number;
  titulo: string;
  descripcion: string;
  cliente: string;
  sucursal: string;
  direccion: string;
  prioridad: string;
  observacionesIniciales: string;
  archivosAdjuntos: Array<{
    nombreOriginal: string;
    nombreArchivo: string;
    tamaño: number;
    tipoMime: string;
  }>;
  fechaCreacion: string;
}

const AuditDetailModal: React.FC<AuditDetailModalProps> = ({ auditId, onClose, onStartAudit }) => {
  const [audit, setAudit] = useState<AuditDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAuditDetail();
  }, [auditId]);

  const loadAuditDetail = async () => {
    try {
      setLoading(true);
      const response = await auditsApi.getById(auditId);
      if (response.data.success) {
        const auditData = response.data.data;
        setAudit({
          id: auditData.id,
          titulo: auditData.titulo,
          descripcion: auditData.descripcion || 'Sin descripción',
          cliente: auditData.client?.nombre || auditData.cliente || 'Cliente desconocido',
          sucursal: auditData.branch?.nombre || auditData.sucursal || 'Sucursal no especificada',
          direccion: auditData.branch?.direccion || auditData.direccion || 'Dirección no especificada',
          prioridad: auditData.prioridad,
          observacionesIniciales: auditData.observacionesIniciales || '',
          archivosAdjuntos: Array.isArray(auditData.archivosAdjuntos) 
            ? auditData.archivosAdjuntos 
            : (typeof auditData.archivosAdjuntos === 'string' 
                ? JSON.parse(auditData.archivosAdjuntos || '[]') 
                : []),
          fechaCreacion: auditData.createdAt
        });
      }
    } catch (err: any) {
      setError('Error cargando detalles: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (filename: string) => {
    try {
      await auditsApi.downloadFile(auditId, filename);
    } catch (err: any) {
      setError('Error descargando archivo: ' + (err.message || 'Error desconocido'));
    }
  };

  const getPriorityColor = (prioridad: string) => {
    switch (prioridad) {
      case 'urgente': return '#dc3545';
      case 'prioritaria': return '#ffc107';
      case 'normal': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getPriorityIcon = (prioridad: string) => {
    switch (prioridad) {
      case 'urgente': return '🔴';
      case 'prioritaria': return '🟡';
      case 'normal': return '🟢';
      default: return '⚪';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div style={{ 
        position: 'fixed', 
        inset: 0, 
        background: 'rgba(0,0,0,0.5)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div>⏳ Cargando detalles de auditoría...</div>
        </div>
      </div>
    );
  }

  if (error || !audit) {
    return (
      <div style={{ 
        position: 'fixed', 
        inset: 0, 
        background: 'rgba(0,0,0,0.5)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '8px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{ color: '#dc3545', marginBottom: '1rem' }}>
            ❌ {error || 'No se pudieron cargar los detalles'}
          </div>
          <button onClick={onClose} style={{
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{ 
        background: 'white', 
        width: '95%',
        maxWidth: '700px',
        borderRadius: '8px',
        overflow: 'hidden',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          background: '#007bff',
          color: 'white',
          padding: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
              📋 Detalles de Auditoría
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
              Auditoría #{audit.id}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'transparent',
            border: '2px solid white',
            color: 'white',
            padding: '0.5rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1.2rem'
          }}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {/* Información Principal */}
          <div style={{
            background: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            border: '1px solid #dee2e6'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '1.2rem' }}>
              📊 Información de la Auditoría
            </h3>
            
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <strong style={{ color: '#495057' }}>📝 Título:</strong>
                <div style={{ fontSize: '1.1rem', fontWeight: '500', marginTop: '0.25rem' }}>
                  {audit.titulo}
                </div>
              </div>

              {audit.descripcion && audit.descripcion !== 'Sin descripción' && (
                <div>
                  <strong style={{ color: '#495057' }}>📄 Descripción:</strong>
                  <div style={{ marginTop: '0.25rem', lineHeight: '1.5' }}>
                    {audit.descripcion}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div>
                  <strong style={{ color: '#495057' }}>🏢 Cliente:</strong>
                  <div style={{ marginTop: '0.25rem' }}>{audit.cliente}</div>
                </div>
                <div>
                  <strong style={{ color: '#495057' }}>🏪 Sucursal:</strong>
                  <div style={{ marginTop: '0.25rem' }}>{audit.sucursal}</div>
                </div>
              </div>

              <div>
                <strong style={{ color: '#495057' }}>📍 Dirección:</strong>
                <div style={{ marginTop: '0.25rem' }}>{audit.direccion}</div>
              </div>

              <div>
                <strong style={{ color: '#495057' }}>🎯 Prioridad:</strong>
                <div style={{ 
                  marginTop: '0.25rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '20px',
                  background: getPriorityColor(audit.prioridad) + '20',
                  color: getPriorityColor(audit.prioridad),
                  fontWeight: '500'
                }}>
                  {getPriorityIcon(audit.prioridad)} {audit.prioridad.toUpperCase()}
                </div>
              </div>

              {audit.observacionesIniciales && (
                <div>
                  <strong style={{ color: '#495057' }}>💬 Observaciones Iniciales:</strong>
                  <div style={{ 
                    marginTop: '0.25rem', 
                    background: '#fff3cd',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #ffeaa7',
                    lineHeight: '1.5'
                  }}>
                    {audit.observacionesIniciales}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Archivos Adjuntos */}
          {audit.archivosAdjuntos.length > 0 && (
            <div style={{
              background: '#e8f4fd',
              padding: '1.5rem',
              borderRadius: '6px',
              marginBottom: '1.5rem',
              border: '1px solid #bee5eb'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#495057', fontSize: '1.2rem' }}>
                📎 Archivos Adjuntos ({audit.archivosAdjuntos.length})
              </h3>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {audit.archivosAdjuntos.map((archivo, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'white',
                      padding: '1rem',
                      borderRadius: '4px',
                      border: '1px solid #b3d9ff',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '500', marginBottom: '0.25rem' }}>
                        📄 {archivo.nombreOriginal}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        {formatFileSize(archivo.tamaño)} • {archivo.tipoMime}
                      </div>
                    </div>
                    <button
                      onClick={() => downloadFile(archivo.nombreArchivo)}
                      style={{
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem'
                      }}
                    >
                      ⬇️ Descargar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Información Adicional */}
          <div style={{
            background: '#f8f9fa',
            padding: '1rem',
            borderRadius: '6px',
            fontSize: '0.9rem',
            color: '#6c757d'
          }}>
            <div>📅 <strong>Auditoría creada:</strong> {new Date(audit.fechaCreacion).toLocaleString()}</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          background: '#f8f9fa',
          padding: '1.5rem',
          borderTop: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
            Revisa la información y archivos antes de iniciar
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={onClose}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={onStartAudit}
              style={{
                background: '#28a745',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              ▶️ INICIAR AUDITORÍA
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditDetailModal;